import { TaskState } from '@modules/TasksModule/types';
import { InboxContext } from '../types';
import TasksModule from '@modules/TasksModule';
import { inboxActions } from '../state';
import InboxModule from '..';
import { SPEED_TO_LEAD_OUTBOUND_TASK_TYPE } from '@modules/SpeedToLeadTasksModule/types';
import { sanitizePhoneNumberTo10Digits } from '@lib/utils/formatPhone/sanitizePhoneNumberTo10Digits';
import { matchesNetworkTestDial } from '@modules/SoftPhoneModule/lib/networkTestDial';

export function setOutboundCallToOffered(
  context: InboxContext,
  sessionId: string,
  outgoingNumber: string,
  attributes: Record<string, any>,
  kazooCallId: string,
) {
  if (matchesNetworkTestDial(outgoingNumber)) {
    context.logger.debug(
      'Skipping outbound task creation for network test dial',
      { outgoingNumber, sessionId },
    );
    return;
  }

  context.logger.info('outbound call Offered', sessionId);

  // Fetch recording status up front (not after task creation) so STL calls,
  // which return early below to avoid creating a duplicate task, still get it (CCPE-619).
  InboxModule.pollRecordingStatus(kazooCallId, attributes.callId);

  let task = TasksModule.findOutboundCallTask(outgoingNumber);

  // we should have a task for this outbound call task already by `TaskModule.createOutboundCallTask`
  if (!task) {
    // STL tasks are already in Accepted state and use a different ID, so findOutboundCallTask
    // (which filters for Pending) won't find them. Check hasMadeCall to confirm this call
    // originated from an STL task — a regular outbound to the same number won't have it set.
    // Both sides are canonicalized to 10 digits because BFF / SIP can disagree on shape
    // (CCP-39831).
    const outgoing10 = sanitizePhoneNumberTo10Digits(outgoingNumber);
    const isStlCall =
      !!outgoing10 &&
      TasksModule.getTasks().some(
        (t) =>
          t.type === SPEED_TO_LEAD_OUTBOUND_TASK_TYPE &&
          t.attributes.hasMadeCall &&
          sanitizePhoneNumberTo10Digits(t.from) === outgoing10,
      );
    if (isStlCall) return;

    // create a task if for some reason it doesn't exist
    task = TasksModule.createTask({
      isInternal: true,
      isStateActive: true,
      id: sessionId,
      from: outgoingNumber,
      displayName: outgoingNumber,
      direction: 'outbound',
      type: 'voice',
      state: TaskState.Pending,
      attributes,
    });
  } else {
    // since the pending task was created with a temporary session id (`tmp-outbound`), update the sessionId for the task
    TasksModule.updateTaskId(task, sessionId);
    TasksModule.updateTaskAttributes(sessionId, attributes);
    TasksModule.updateTaskIsStateActive(sessionId, true);
  }
  TasksModule.setTaskState(sessionId, TaskState.Offered);
  task.startedAt = new Date();
}

export function setOutboundCallToAccepted(
  context: InboxContext,
  taskId: string,
  kazooCallId?: string,
) {
  context.logger.info('outbound call Accepted', taskId);

  // we want to try both session id and call id in case pcsConversationSid hasn't been fetched yet
  const task =
    TasksModule.getTask(taskId) ?? TasksModule.getTaskByCallId(taskId);

  if (task) {
    TasksModule.setTaskState(task.id, TaskState.Accepted);

    // A direct outbound call's recording can only start once PCS sees the
    // external bridge answer, which can happen well after the Offered-time
    // poll above gave up if the callee took a while to pick up. Restart the
    // poll now that the webphone has reported the call as answered.
    if (kazooCallId) {
      InboxModule.pollRecordingStatus(kazooCallId, task.attributes.callId);
    }
  }
}

export function setOutboundCallToRejected(
  context: InboxContext,
  sessionId: string,
) {
  context.logger.info('outbound call Rejected', sessionId);
  const task = TasksModule.getTask(sessionId);
  if (task) {
    TasksModule.setTaskState(sessionId, TaskState.Rejected);
    TasksModule.deleteTask(sessionId);
    inboxActions.restorePrevSelectedTaskId();
  }
}

export function setOutboundCallToCompleted(
  context: InboxContext,
  sessionId: string,
) {
  context.logger.info('outbound call Completed', sessionId);

  TasksModule.setTaskState(sessionId, TaskState.Completed);
  TasksModule.deleteTask(sessionId);
  inboxActions.restorePrevSelectedTaskId();
}
